// src/pages/club/settings/subscription/steps/ClubStep3.jsx
import "./ClubStep3.scss";

import * as formik from "formik";
import * as yup from "yup";

import { Col, Form, Row } from "react-bootstrap";
import { useMemo, useState } from "react";

import AppButton from "../../../../../commons/buttons/AppButton";
import AppContainer from "../../../../../commons/containers/AppContainer";
import AppInputV2 from "../../../../../commons/inputs/AppInputV2";
import AppLinkButton from "../../../../../commons/buttons/AppLinkButton";
import AppTextAreaV2 from "../../../../../commons/inputs/AppTextAreaV2";
import HallAppToast from "../../../../../commons/HallAppToast";

const { Formik } = formik;

/**
 * Step 3 - Regras do Clube
 * - Acúmulo
 * - Expiração (crédito/serviço)
 * - Frequência de uso (Semanal/Quinzenal/Mensal/Por ciclo) + seleção de dias
 * - Texto opcional
 *
 * payload:
 * nextStep({
 *   rules: {
 *     autoRenew: boolean,
 *     allowCarryOver: boolean,
 *     carryOverLimitCycles: number|null,
 *     creditExpiresInDays: number|null,
 *     includedServiceExpiresInDays: number|null,
 *
 *     redemption: {
 *       enabled: boolean,
 *       qty: number, // default 1
 *       frequency: "WEEKLY"|"FORTNIGHTLY"|"MONTHLY"|"CYCLE"|null,
 *       weeks: string[] // WEEKLY multi, FORTNIGHTLY single opcional, senão []
 *     },
 *
 *     termsText: string|null,
 *
 *     // compat opcional com backend legado:
 *     maxRedemptionsPerCycle: number|null
 *   }
 * })
 */

function ClubStep3({ nextStep, toBack, data }) {
  const rules = data?.rules || {};

  // ---------- frequência (state externo, igual seu exemplo) ----------
  const [frequency, setFrequency] = useState(rules?.redemption?.frequency || null);
  const [weeks, setWeeks] = useState(() => rules?.redemption?.weeks || []);

  const weekOptions = useMemo(
    () => [
      { key: "SUNDAY", label: "Dom" },
      { key: "MONDAY", label: "Seg" },
      { key: "TUESDAY", label: "Ter" },
      { key: "WEDNESDAY", label: "Qua" },
      { key: "THURSDAY", label: "Qui" },
      { key: "FRIDAY", label: "Sex" },
      { key: "SATURDAY", label: "Sáb" },
    ],
    []
  );

  const frequencyOptions = useMemo(
    () => [
      { key: "WEEKLY", label: "Semanal", hint: "1x por semana (dias da semana)" },
      { key: "FORTNIGHTLY", label: "Quinzenal", hint: "A cada 15 dias (dia opcional)" },
      { key: "MONTHLY", label: "Mensal", hint: "1x por mês" },
      { key: "CYCLE", label: "Por ciclo", hint: "Mensal/Trimestral/Anual do plano" },
    ],
    []
  );

  function handleWeeks(value) {
    // SEMANAL => multi
    if (frequency === "WEEKLY") {
      setWeeks((prev) => (prev.includes(value) ? prev.filter((d) => d !== value) : [...prev, value]));
      return;
    }

    // QUINZENAL => single opcional (pode desmarcar clicando novamente)
    if (frequency === "FORTNIGHTLY") {
      setWeeks((prev) => (prev[0] === value ? [] : [value]));
      return;
    }

    // outros => não usa weeks
    setWeeks([]);
  }

  const showWeeksWeekly = frequency === "WEEKLY";
  const showWeeksFortnightly = frequency === "FORTNIGHTLY";

  // ---------- formik (demais regras) ----------
  const initialValues = useMemo(
    () => ({
      autoRenew: rules?.autoRenew ?? true,

      allowCarryOver: rules?.allowCarryOver ?? false,
      carryOverLimitCycles: rules?.carryOverLimitCycles ?? "",

      creditExpiresEnabled: rules?.creditExpiresInDays != null,
      creditExpiresInDays: rules?.creditExpiresInDays ?? "",

      includedServiceExpiresEnabled: rules?.includedServiceExpiresInDays != null,
      includedServiceExpiresInDays: rules?.includedServiceExpiresInDays ?? "",

      // frequência (switch)
      redemptionEnabled: rules?.redemption?.enabled ?? false,

      termsText: rules?.termsText ?? "",
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.rules]
  );

  const schema = yup.object().shape({
    carryOverLimitCycles: yup.string().when("allowCarryOver", {
      is: true,
      then: (s) =>
        s
          .required("Informe por quantos ciclos pode acumular")
          .matches(/^\d+$/, "Use apenas números")
          .test("min1", "Mínimo 1", (v) => Number(v) >= 1),
      otherwise: (s) => s.notRequired().nullable(true),
    }),

    creditExpiresInDays: yup.string().when("creditExpiresEnabled", {
      is: true,
      then: (s) =>
        s
          .required("Informe em quantos dias expira")
          .matches(/^\d+$/, "Use apenas números")
          .test("min1", "Mínimo 1", (v) => Number(v) >= 1),
      otherwise: (s) => s.notRequired().nullable(true),
    }),

    includedServiceExpiresInDays: yup.string().when("includedServiceExpiresEnabled", {
      is: true,
      then: (s) =>
        s
          .required("Informe em quantos dias expira")
          .matches(/^\d+$/, "Use apenas números")
          .test("min1", "Mínimo 1", (v) => Number(v) >= 1),
      otherwise: (s) => s.notRequired().nullable(true),
    }),

    termsText: yup.string().max(1200, "Máx. 1200 caracteres"),
  });

  const toNext = (values) => {
    // validações UX
    if (values.allowCarryOver && !values.carryOverLimitCycles) {
      HallAppToast.error("Informe por quantos ciclos o benefício pode acumular.");
      return;
    }
    if (values.creditExpiresEnabled && !values.creditExpiresInDays) {
      HallAppToast.error("Informe em quantos dias o crédito expira.");
      return;
    }
    if (values.includedServiceExpiresEnabled && !values.includedServiceExpiresInDays) {
      HallAppToast.error("Informe em quantos dias o serviço incluso expira.");
      return;
    }

    // frequência de uso
    if (values.redemptionEnabled) {
      if (!frequency) {
        HallAppToast.error("Selecione a frequência de uso.");
        return;
      }

      if (frequency === "WEEKLY" && weeks.length === 0) {
        HallAppToast.error("Selecione ao menos um dia da semana.");
        return;
      }
    }

    const redemptionPayload = values.redemptionEnabled
      ? {
          enabled: true,
          qty: 1,
          frequency,
          weeks: frequency === "WEEKLY" || frequency === "FORTNIGHTLY" ? weeks : [],
        }
      : {
          enabled: false,
          qty: 1,
          frequency: null,
          weeks: [],
        };

    const payload = {
      rules: {
        autoRenew: !!values.autoRenew,

        allowCarryOver: !!values.allowCarryOver,
        carryOverLimitCycles: values.allowCarryOver ? Number(values.carryOverLimitCycles) : null,

        creditExpiresInDays: values.creditExpiresEnabled ? Number(values.creditExpiresInDays) : null,
        includedServiceExpiresInDays: values.includedServiceExpiresEnabled
          ? Number(values.includedServiceExpiresInDays)
          : null,

        redemption: redemptionPayload,

        // compat opcional: se escolher "por ciclo", preenche legado
        maxRedemptionsPerCycle:
          values.redemptionEnabled && frequency === "CYCLE" ? 1 : null,

        termsText: (values.termsText || "").trim() || null,
      },
    };

    nextStep(payload);
  };

  return (
    <div className="club-step3" style={{ paddingBottom: 110 }}>
      <div className="mb-3">
        <div className="club-step3__title">Regras do Clube</div>
        <div className="club-step3__subtitle text-muted">
          Defina regras simples de uso (expiração, acúmulo e frequência).
        </div>
      </div>

      <Formik validationSchema={schema} initialValues={initialValues} enableReinitialize>
        {({ values, setFieldValue, handleChange, errors, touched }) => (
          <Form>
            {/* Card: Acúmulo */}
            <div className="club-step3__card">
              <div className="club-step3__card-title">Acúmulo de benefícios</div>
              <div className="club-step3__card-hint text-muted">
                Defina se o benefício não utilizado pode acumular para o próximo ciclo.
              </div>

              <Form.Check
                type="switch"
                id="club-carry-over"
                label="Permitir acumular benefícios"
                checked={!!values.allowCarryOver}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setFieldValue("allowCarryOver", enabled);
                  if (!enabled) setFieldValue("carryOverLimitCycles", "");
                }}
              />

              {values.allowCarryOver && (
                <Row className="g-2 mt-2">
                  <Col xs={12} sm={6}>
                    <AppInputV2
                      label="Acumular por quantos ciclos?"
                      name="carryOverLimitCycles"
                      isRequired
                      type="number"
                      placeholder="Ex: 1"
                      value={values.carryOverLimitCycles}
                      onChange={handleChange}
                      errors={errors}
                      touched={touched}
                    />
                    <div className="club-step3__hint-mini text-muted">
                      Ex: “1” = pode usar no próximo ciclo, depois expira.
                    </div>
                  </Col>
                </Row>
              )}
            </div>

            {/* Card: Expiração */}
            <div className="club-step3__card">
              <div className="club-step3__card-title">Expiração</div>
              <div className="club-step3__card-hint text-muted">
                Opcional: defina prazo para expiração do crédito e do serviço incluso.
              </div>

              <Row className="g-2">
                <Col xs={12} md={6}>
                  <Form.Check
                    type="switch"
                    id="credit-expires"
                    label="Crédito expira"
                    checked={!!values.creditExpiresEnabled}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setFieldValue("creditExpiresEnabled", enabled);
                      if (!enabled) setFieldValue("creditExpiresInDays", "");
                    }}
                  />

                  {values.creditExpiresEnabled && (
                    <div className="mt-2">
                      <AppInputV2
                        label="Expira em (dias)"
                        name="creditExpiresInDays"
                        isRequired
                        type="number"
                        placeholder="Ex: 30"
                        value={values.creditExpiresInDays}
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                      />
                    </div>
                  )}
                </Col>

                <Col xs={12} md={6}>
                  <Form.Check
                    type="switch"
                    id="service-expires"
                    label="Serviço incluso expira"
                    checked={!!values.includedServiceExpiresEnabled}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setFieldValue("includedServiceExpiresEnabled", enabled);
                      if (!enabled) setFieldValue("includedServiceExpiresInDays", "");
                    }}
                  />

                  {values.includedServiceExpiresEnabled && (
                    <div className="mt-2">
                      <AppInputV2
                        label="Expira em (dias)"
                        name="includedServiceExpiresInDays"
                        isRequired
                        type="number"
                        placeholder="Ex: 30"
                        value={values.includedServiceExpiresInDays}
                        onChange={handleChange}
                        errors={errors}
                        touched={touched}
                      />
                    </div>
                  )}
                </Col>
              </Row>

              <div className="club-step3__hint-mini text-muted mt-2">
                Se não habilitar, os benefícios ficam válidos até a próxima renovação (ou até você definir regra no backend).
              </div>
            </div>

            {/* ✅ Card: Frequência de uso (do seu exemplo) */}
            <div
              style={{
                background: "#fff",
                border: "1px solid #eee",
                borderRadius: 16,
                padding: 14,
                marginBottom: 12,
              }}
            >
              <div className="d-flex align-items-center justify-content-between gap-2">
                <div>
                  <div className="mb-1" style={{ fontSize: 14, fontWeight: 700 }}>
                    Com que frequência?
                  </div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    Semanal permite vários dias. Quinzenal permite escolher 1 dia (opcional).
                  </div>
                </div>

                <Form.Check
                  type="switch"
                  id="redemption-enabled"
                  label=""
                  checked={!!values.redemptionEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setFieldValue("redemptionEnabled", enabled);

                    if (!enabled) {
                      setFrequency(null);
                      setWeeks([]);
                      return;
                    }

                    // default ao ligar
                    if (!frequency) setFrequency("MONTHLY");
                    if (!weeks?.length) setWeeks([]);
                  }}
                />
              </div>

              {values.redemptionEnabled && (
                <>
                  <hr className="my-3" />

                  <Row className="g-2">
                    {frequencyOptions.map((opt) => {
                      const active = frequency === opt.key;
                      return (
                        <Col xs={6} md={3} key={opt.key}>
                          <button
                            type="button"
                            onClick={() => {
                              setFrequency(opt.key);

                              // ✅ limpa weeks conforme troca
                              if (opt.key === "WEEKLY") {
                                // mantém como está (multi)
                                return;
                              }
                              if (opt.key === "FORTNIGHTLY") {
                                // se estava semanal com vários, reduz pra 1 (opcional manter o primeiro)
                                setWeeks((prev) => (prev?.length > 0 ? [prev[0]] : []));
                                return;
                              }
                              // outros: não usa weeks
                              setWeeks([]);
                            }}
                            className="w-100"
                            style={{
                              borderRadius: 14,
                              border: active ? "2px solid #2f2a7a" : "1px solid #e8e8f0",
                              background: active ? "#f1f3ff" : "#fff",
                              padding: "12px 10px",
                              textAlign: "left",
                              cursor: "pointer",
                            }}
                          >
                            <div style={{ fontSize: 13, fontWeight: 700, color: "#1d1b4a" }}>
                              {opt.label}
                            </div>
                            <div className="text-muted" style={{ fontSize: 11 }}>
                              {opt.hint}
                            </div>
                          </button>
                        </Col>
                      );
                    })}
                  </Row>

                  {/* Dias da semana (SEMANAL) */}
                  {showWeeksWeekly && (
                    <>
                      <hr className="my-3" />
                      <div className="mb-2" style={{ fontSize: 13, fontWeight: 700 }}>
                        Dias da semana <span style={{ color: "red" }}>*</span>
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        {weekOptions.map((w) => {
                          const selected = weeks.includes(w.key);
                          return (
                            <button
                              key={w.key}
                              type="button"
                              onClick={() => handleWeeks(w.key)}
                              style={{
                                borderRadius: 999,
                                border: selected ? "1px solid #2f2a7a" : "1px solid #e8e8f0",
                                background: selected ? "#2f2a7a" : "#fff",
                                color: selected ? "#fff" : "#1d1b4a",
                                padding: "8px 12px",
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: "pointer",
                              }}
                            >
                              {w.label}
                            </button>
                          );
                        })}
                      </div>
                      {weeks.length === 0 && (
                        <div className="text-muted mt-2" style={{ fontSize: 11 }}>
                          Selecione pelo menos um dia.
                        </div>
                      )}
                    </>
                  )}

                  {/* Dias da semana (QUINZENAL - 1 dia opcional) */}
                  {showWeeksFortnightly && (
                    <>
                      <hr className="my-3" />
                      <div className="mb-2" style={{ fontSize: 13, fontWeight: 700 }}>
                        Dia preferencial (opcional)
                      </div>
                      <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                        Se selecionar um dia, o uso será liberado sempre nesse dia da semana.
                        Se não selecionar, será a cada 15 dias a partir do início.
                      </div>

                      <div className="d-flex flex-wrap gap-2">
                        {weekOptions.map((w) => {
                          const selected = weeks[0] === w.key;
                          return (
                            <button
                              key={w.key}
                              type="button"
                              onClick={() => handleWeeks(w.key)}
                              style={{
                                borderRadius: 999,
                                border: selected ? "1px solid #2f2a7a" : "1px solid #e8e8f0",
                                background: selected ? "#2f2a7a" : "#fff",
                                color: selected ? "#fff" : "#1d1b4a",
                                padding: "8px 12px",
                                fontSize: 12,
                                fontWeight: 700,
                                cursor: "pointer",
                              }}
                            >
                              {w.label}
                            </button>
                          );
                        })}
                      </div>

                      {weeks.length === 0 && (
                        <div className="text-muted mt-2" style={{ fontSize: 11 }}>
                          Sem dia selecionado: será a cada 15 dias a partir do início.
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {/* Card: Texto de termos */}
            <div className="club-step3__card">
              <div className="club-step3__card-title">Texto para o cliente (opcional)</div>
              <div className="club-step3__card-hint text-muted">
                Um texto simples que aparece no checkout/site para reduzir dúvidas.
              </div>

              <AppTextAreaV2
                label="Termos e regras"
                name="termsText"
                value={values.termsText}
                onChange={handleChange}
                errors={errors}
                touched={touched}
                placeholder="Ex: O crédito deve ser usado dentro do mês. Agendamentos sujeitos à disponibilidade…"
              />

              <div className="club-step3__hint-mini text-muted">
                Dica: mantenha curto e claro. Regras complexas você pode aplicar no backend.
              </div>
            </div>

            {/* Footer */}
            <div className="club-step3__footer">
              <AppContainer>
                <AppLinkButton name="Próximo" fullWidth={true} onClick={() => toNext(values)} />
                <div style={{ marginTop: 10 }}>
                  <AppButton name="Voltar" width="100%" onClick={toBack} />
                </div>
              </AppContainer>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}

export default ClubStep3;
